import type { CSSProperties } from 'react';
import { cn } from '~/lib/utils';
import styles from './TrustedByLogos.module.css';

const DEFAULT_TRUSTED_LOGOS = [
  { src: '/landing/trusted-logos/nike.svg', alt: 'Nike' },
  { src: '/landing/trusted-logos/google.svg', alt: 'Google' },
  { src: '/landing/trusted-logos/microsoft.svg', alt: 'Microsoft' },
  { src: '/landing/trusted-logos/amazon.svg', alt: 'Amazon' },
  { src: '/landing/trusted-logos/meta.svg', alt: 'Meta' },
] as const;

/** `.logoSlot` is `--spacing-192`, and the loop below halves the track, so one pass has to be wider
 *  than any viewport it plays on or the strip runs out of logos mid-screen. */
const SLOT_WIDTH = 192;
const MIN_PASS_WIDTH = 2400;

export interface TrustedLogo {
  src: string;
  alt: string;
}

export interface TrustedByLogosProps {
  className?: string;
  caption?: string;
  logos?: readonly TrustedLogo[];
  logoMarqueeDurationSec?: number;
  /** `dark` is the landing v2 ground: same strip, the fades and caption in the dark ramp. */
  tone?: 'light' | 'dark';
}

export const TrustedByLogos = ({
  className,
  caption = 'Trusted by individuals at',
  logos = DEFAULT_TRUSTED_LOGOS,
  logoMarqueeDurationSec = 45,
  tone = 'light',
}: TrustedByLogosProps) => {
  const passes = Math.max(1, Math.ceil(MIN_PASS_WIDTH / Math.max(1, logos.length * SLOT_WIDTH)));
  const pass = Array.from({ length: passes }, () => logos).flat();
  const loopLogos = [...pass, ...pass];

  return (
    <div
      className={cn(styles.trustedBy, tone === 'dark' && styles.trustedByDark, className)}
      data-name="TrustedByLogos"
      role="region"
      aria-label={caption}
    >
      <p className={styles.trustedCaption}>{caption}</p>
      <div
        className={styles.logoMarquee}
        style={
          {
            '--logo-marquee-duration': `${logoMarqueeDurationSec}s`,
          } as CSSProperties
        }
      >
        <div className={styles.logoMarqueeEdgeLeft} aria-hidden />
        <div className={styles.logoMarqueeEdgeRight} aria-hidden />
        <div className={styles.logoMarqueeViewport}>
          <div className={styles.logoMarqueeTrack}>
            {loopLogos.map((logo, index) => (
              <div key={`${logo.alt}-${index}`} className={styles.logoSlot}>
                <img className={styles.logoImg} src={logo.src} alt="" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

TrustedByLogos.displayName = 'TrustedByLogos';
